import { X } from 'lucide-react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { listChannels } from '../../api/channels';
import { FUNNEL_STAGE_LABELS, FUNNEL_STAGES, type DialogDto, type DialogListFilters } from '../../api/types';
import { useTeamMembers } from '../../hooks/use-team';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select } from '../ui/select';

export interface DialogFiltersProps {
  value: DialogListFilters;
  onChange: (next: DialogListFilters) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Currently loaded page, used to compute local option counts — the API has no filter-count endpoint. */
  items?: DialogDto[];
}

const STATE_LABELS: Record<string, string> = { AGENT_ACTIVE: 'Агент', HUMAN_ACTIVE: 'Человек', CLOSED: 'Закрыт' };
const SLA_LABELS: Record<string, string> = { ok: 'ok', warn: 'warn', breach: 'breach', escalated: 'escalated' };

function count<T>(items: T[], predicate: (item: T) => boolean): number {
  return items.reduce((n, item) => (predicate(item) ? n + 1 : n), 0);
}

export function DialogFilters({ value, onChange, search, onSearchChange, items = [] }: DialogFiltersProps) {
  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const team = useTeamMembers();

  function set<K extends keyof DialogListFilters>(key: K, next: DialogListFilters[K]) {
    onChange({ ...value, [key]: next || undefined, cursor: undefined });
  }

  const chips = useMemo(() => {
    const list: { key: keyof DialogListFilters; label: string }[] = [];
    if (value.channel_id) {
      const ch = channels.data?.find((c) => c.id === value.channel_id);
      list.push({ key: 'channel_id', label: `Канал: ${ch?.title ?? value.channel_id}` });
    }
    if (value.state) list.push({ key: 'state', label: `Статус: ${STATE_LABELS[value.state] ?? value.state}` });
    if (value.stage) {
      const stageLabel = FUNNEL_STAGE_LABELS[value.stage as keyof typeof FUNNEL_STAGE_LABELS] ?? value.stage;
      list.push({ key: 'stage', label: `Стадия: ${stageLabel}` });
    }
    if (value.agent_id) {
      const member = team.data?.members.find((m) => m.userId === value.agent_id);
      list.push({ key: 'agent_id', label: `Ответственный: ${member?.name ?? value.agent_id}` });
    }
    if (value.sla) list.push({ key: 'sla', label: `SLA: ${SLA_LABELS[value.sla] ?? value.sla}` });
    if (value.awaiting_human === 'true') list.push({ key: 'awaiting_human', label: 'Ждёт ответа' });
    return list;
  }, [value, channels.data, team.data]);

  return (
    <div className="flex flex-col gap-2 border-b border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-56">
          <Input
            placeholder="Имя, фамилия или VK ID…"
            className="pr-7"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Поиск диалогов"
          />
          {search && (
            <button
              type="button"
              aria-label="Очистить поиск"
              onClick={() => onSearchChange('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-fg hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select
          aria-label="Канал"
          value={value.channel_id ?? ''}
          onChange={(e) => set('channel_id', e.target.value || undefined)}
        >
          <option value="">Все каналы</option>
          {(channels.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.title} ({count(items, (d) => d.channelId === c.id)})
            </option>
          ))}
        </Select>
        <Select aria-label="Статус" value={value.state ?? ''} onChange={(e) => set('state', e.target.value as never)}>
          <option value="">Любой статус</option>
          {Object.entries(STATE_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label} ({count(items, (d) => d.state === v)})
            </option>
          ))}
        </Select>
        <Select aria-label="Стадия воронки" value={value.stage ?? ''} onChange={(e) => set('stage', e.target.value)}>
          <option value="">Любая стадия</option>
          {FUNNEL_STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {FUNNEL_STAGE_LABELS[stage]} ({count(items, (d) => d.contact.funnelStage === stage)})
            </option>
          ))}
        </Select>
        <Select
          aria-label="Ответственный"
          value={value.agent_id ?? ''}
          onChange={(e) => set('agent_id', e.target.value || undefined)}
        >
          <option value="">Любой ответственный</option>
          {(team.data?.members ?? []).map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name} ({count(items, (d) => d.assigneeUserId === m.userId)})
            </option>
          ))}
        </Select>
        <Select aria-label="SLA" value={value.sla ?? ''} onChange={(e) => set('sla', e.target.value as never)}>
          <option value="">Любой SLA</option>
          {Object.entries(SLA_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label} ({count(items, (d) => d.slaStatus === v)})
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1 text-sm text-muted-fg">
          <input
            type="checkbox"
            checked={value.awaiting_human === 'true'}
            onChange={(e) => set('awaiting_human', e.target.checked ? 'true' : undefined)}
          />
          Ждёт ответа человека ({count(items, (d) => d.awaitingHuman)})
        </label>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <Badge key={chip.key} className="gap-1">
              {chip.label}
              <button type="button" aria-label={`Сбросить фильтр: ${chip.label}`} onClick={() => set(chip.key, undefined)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => onChange({ limit: value.limit })}
          >
            Сбросить всё
          </button>
        </div>
      )}
    </div>
  );
}
